import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { select, Store } from '@ngrx/store';
import * as JobDetailSelectors from 'src/admin-app/jobs/job-detail/+state/job-detail.selectors';
import {
	OtherChargeItem,
	AccountingClient,
	Job,
	ServiceType,
} from 'src/shared/api';
import {
	map,
	takeUntil,
	tap,
	exhaustMap,
	distinctUntilChanged,
	filter,
} from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { RailProsError } from 'src/shared/models/rail-pros-error.model';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { JobDetailPartialState } from 'src/admin-app/jobs/job-detail/+state/job-detail.reducer';

@Component({
	selector: 'rpfsa-other-charges',
	templateUrl: './other-charge-list.component.html',
	styleUrls: ['./other-charge-list.component.scss'],
})
export class OtherChargeListComponent extends SelfDestruct implements OnInit {
	@Input() jobId: number;
	@Input() otherCharges: OtherChargeItem[] = [];
	@Output() refresh = new EventEmitter<void>();

	readonly job$ = this.store.pipe(
		select(JobDetailSelectors.getSelectedJob),
		filter<Job>(Boolean)
	);

	serviceTypes$ = this.job$.pipe(
		map((job) => job.contractedRates.map((s) => s.serviceType))
	);

	displayedColumns: string[] = [
		'autoRenew',
		'description',
		'chargeDate',
		'units',
		'unitPrice',
		'amount',
		'serviceType',
		'action',
	];

	otherChargeItemChanges = new Subject<OtherChargeItem>();
	otherChargeItemDeletes = new Subject<number>();
	showOtherChargeFields = new BehaviorSubject(null);
	showOtherChargeFields$ = this.showOtherChargeFields.pipe(
		map((v) => v !== null && v > -2)
	);

	otherChargeItem = new FormControl('') as FormControl;
	serviceTypes: ServiceType[];

	constructor(
		private readonly accountingClient: AccountingClient,
		private readonly store: Store<JobDetailPartialState>
	) {
		super();
	}

	ngOnInit() {
		this.setupOtherChargeItemChanges();
		this.setupOtherChargeItemDeletes();
		this.serviceTypes$.subscribe(
			(serviceTypes) => (this.serviceTypes = serviceTypes)
		);
	}

	setupOtherChargeItemChanges() {
		this.otherChargeItemChanges
			.pipe(
				takeUntil(this.destroyed),
				map((oc) => {
					oc.jobId = this.jobId;
					return oc;
				}),
				exhaustMap((oc) =>
					this.accountingClient.saveOtherChargeItem(oc)
				),
				tap(() => this.refresh.emit())
			)
			.subscribe(() => {
				this.clearAndHideOtherChargesForm();
			});
	}

	setupOtherChargeItemDeletes() {
		this.otherChargeItemDeletes
			.pipe(
				takeUntil(this.destroyed),
				distinctUntilChanged(),
				filter((id) => id !== -1),
				exhaustMap((id) =>
					this.accountingClient.deleteOtherChargeItem(id)
				)
			)
			.subscribe();
	}

	saveOtherCharge() {
		const otherChargeEdits = this.otherChargeItem.value;
		// send add/update to server
		this.otherChargeItemChanges.next(otherChargeEdits);
	}

	deleteOtherCharge(i: number): void {
		const otherCharges = this.otherCharges;
		const otherchargeItem = otherCharges[i];
		if (!otherchargeItem) {
			throw new RailProsError(
				100,
				'',
				'Other charge does not exist.  Please refresh the page and try again.'
			);
		}

		this.otherChargeItemDeletes.next(otherchargeItem.id);
		this.refresh.next();
	}

	cancelOtherCharge(): void {
		this.clearAndHideOtherChargesForm();
	}

	addOtherCharge(): void {
		this.showOtherChargeFields.next(-1);
	}

	editOtherCharge(otherChargeItem: OtherChargeItem, i: number): void {
		// destroy previous edit instance first
		this.showOtherChargeFields.next(null);
		this.showOtherChargeFields.next(i);
		this.otherChargeItem.setValue(otherChargeItem, { emitEvent: false });
	}
	private clearAndHideOtherChargesForm() {
		this.otherChargeItem.setValue('', { emitEvent: false });
		this.showOtherChargeFields.next(null);
	}
}
